import { AxiosResponse } from 'axios';
import Select, { components } from 'react-select';
import moment from 'moment';

import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useLoadingStore from 'zustands/useLoadingStore';

import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as QuestionIcon } from 'assets/icons/payroll-register-question.svg';
import MonthInput from 'common/month-input/month-input';
import { IOption } from 'types/common';
import AgGrid from 'components/AgGrid';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { addComma } from 'constants/utils';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import useStoreStore from 'zustands/useStoreStore';
import { IPayrollRegisterEmpInfo, IPayrollRegisterWeeklyHolidayPayDetail } from '../../types/payroll-register';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IHeaderParams } from 'ag-grid-community';
import AllowanceDetailModal from './allowance-detail-modal/allowance-detail-modal';
import WeeklyHolidayPayModal from './weekly-holiday-pay-modal/weekly-holiday-pay-modal';
import Button from 'common/button/button';

// 커스텀 헤더 컴포넌트
const CustomHeaderWithSubtext = (
  props: IHeaderParams & { mainText: string; subText: string; tooltip?: string; tooltipIcon?: React.ReactNode },
) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setShowTooltip(true);
  };

  return (
    <div className="relative flex min-w-[80px] items-center justify-center">
      {props.tooltip && props.tooltipIcon && (
        <div
          ref={iconRef}
          className="mr-1 flex cursor-pointer items-center"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}>
          {props.tooltipIcon}
        </div>
      )}
      <span>{props.mainText}</span>
      <span className="text-[0.85em] text-[#999]">{props.subText}</span>
      {showTooltip && props.tooltip && (
        <div
          className="pointer-events-none fixed z-[9999] min-w-[200px] -translate-x-1/2 whitespace-pre-line rounded bg-[#505054] px-3 py-2 text-left text-sm font-normal leading-5 text-white"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}>
          {props.tooltip}
        </div>
      )}
    </div>
  );
};

const PayrollRegister: React.FunctionComponent = () => {
  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { storeName } = useStoreStore();

  const { checkErrorLog } = useErrorLog();
  const { webApiNode } = useApi();

  const [currentMonth, setCurrentMonth] = useState<Date>(moment().toDate()); // 선택한 월
  const [payType, setPayType] = useState<IOption>({
    // 선택한 급여유형
    value: 'hour',
    label: '시급',
  });
  const [calDay, setCalDay] = useState<string>(''); // 급여정산일
  const [calPeriodStart, setCalPeriodStart] = useState<Date>(new Date()); // 해당월 급여 시작일
  const [calPeriodEnd, setCalPeriodEnd] = useState<Date>(new Date()); // 해당월 급여 종료일

  const [columns, setColumns] = useState<(ColDef | ColGroupDef)[]>([]);
  const [payData, setPayData] = useState<IPayrollRegisterEmpInfo[]>([]);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState<any[]>([]);

  // 수당 및 차감 내역 모달 관련 state
  const [detailModal, setDetailModal] = useState<{
    isVisible: boolean;
    data: { date: string; time: number; pay: number }[];
    title: string;
    infoTitle?: string;
    infoDescriptions?: string[];
  }>({
    isVisible: false,
    data: [],
    title: '',
    infoTitle: '',
    infoDescriptions: [],
  });

  // 주휴수당 모달 관련 state
  const [weeklyHolidayPayModal, setWeeklyHolidayPayModal] = useState<{
    isVisible: boolean;
    data: IPayrollRegisterWeeklyHolidayPayDetail[];
  }>({
    isVisible: false,
    data: [],
  });

  const fetchEmpsPay = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/pay/getEmpsPay`, {
        YEAR: moment(currentMonth).year(),
        MONTH: moment(currentMonth).format('MM'), //01 02 03 04 05 06 07 08 09 10 11 12
        PAY_TYPE: payType?.value === 'hour' ? '0' : payType?.value === 'month' ? '2' : '1',
      });
      if (data.resultCode === 0) {
        const empData = data.resultData.empinfo.map((item: IPayrollRegisterEmpInfo, index: number) => ({
          ...item,
          NUMBER: index + 1,
        }));

        setPayData(empData);

        setCalDay(data.resultData.storeinfo.STORE_DAY);
        setCalPeriodStart(data.resultData.storeinfo.START);
        setCalPeriodEnd(data.resultData.storeinfo.END);
        // 합계 계산
        if (empData.length > 0) {
          const totalRow = {
            employee_name: '합계',
            base_pay: empData.reduce((sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.base_pay || 0), 0),
            weekly_holiday_pay_total: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.weekly_holiday_pay_total || 0),
              0,
            ),
            daily_overtime_pay_total: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.daily_overtime_pay_total || 0),
              0,
            ),
            night_overtime_pay_total: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.night_overtime_pay_total || 0),
              0,
            ),
            holiday_work_pay_total: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.holiday_work_pay_total || 0),
              0,
            ),
            total_allowances: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.total_allowances || 0),
              0,
            ),
            late_deduction: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.late_deduction || 0),
              0,
            ),
            early_deduction: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.early_deduction || 0),
              0,
            ),
            absence_deduction: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.absence_deduction || 0),
              0,
            ),
            off_day_deduction: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.off_day_deduction || 0),
              0,
            ),
            total_deductions: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.total_deductions || 0),
              0,
            ),
            total_salary: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.total_salary || 0),
              0,
            ),
            pension_pay: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.pension_pay || 0),
              0,
            ),
            health_pay: empData.reduce((sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.health_pay || 0), 0),
            long_term_care_pay: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.long_term_care_pay || 0),
              0,
            ),
            employment_pay: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.employment_pay || 0),
              0,
            ),
            income_tax: empData.reduce((sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.income_tax || 0), 0),
            local_income_tax: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.local_income_tax || 0),
              0,
            ),
            total_withholdings: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.total_withholdings || 0),
              0,
            ),
            total_payment_amount: empData.reduce(
              (sum: number, item: IPayrollRegisterEmpInfo) => sum + (item.total_payment_amount || 0),
              0,
            ),
          };

          setPinnedBottomRowData([totalRow]);
        } else {
          setPinnedBottomRowData([]);
        }
      } else {
        throw new Error('/pay/getEmpsPay');
      }
    } catch (err) {
      checkErrorLog(err, 'payroll-register /pay/getEmpsPay');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const handleDateChange = (date: any) => {
    setCurrentMonth(date);
  };

  // 수당 및 차감 내역 모달 열기
  const openDetailModal = (
    data: { date: string; time: number; pay: number }[],
    title: string,
    infoDescriptions: string[] = [],
  ) => {
    setDetailModal({
      isVisible: true,
      data,
      title,
      infoTitle: '확인해주세요.',
      infoDescriptions,
    });
  };

  // 수당 및 차감 내역 모달 닫기
  const closeDetailModal = () => {
    setDetailModal({
      isVisible: false,
      data: [],
      title: '',
      infoTitle: '',
      infoDescriptions: [],
    });
  };

  // 주휴수당 모달 열기
  const openWeeklyHolidayPayModal = (data: IPayrollRegisterWeeklyHolidayPayDetail[]) => {
    setWeeklyHolidayPayModal({
      isVisible: true,
      data,
    });
  };

  // 주휴수당 모달 닫기
  const closeWeeklyHolidayPayModal = () => {
    setWeeklyHolidayPayModal({
      isVisible: false,
      data: [],
    });
  };

  const colorStyles = {
    control: (styles: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '40px',
      fontSize: '14px',
      fontWeight: '400',
      color: '#868686',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  const options = [
    { value: 'hour', label: '시급' },
    { value: 'month', label: '월급' },
    { value: 'day', label: '일급' },
  ];

  const handlePayrollInformation = (selectedOption: any) => {
    setPayType(selectedOption);
  };

  // 분 → "X시간 Y분" (표와 동일 포맷, 엑셀 내보내기용)
  const formatMinutesToTimeText = (minutes: number | null | undefined): string => {
    if (minutes == null || minutes === undefined) return '';
    const m = Number(minutes) || 0;
    const hours = Math.floor(m / 60);
    const mins = m % 60;
    if (mins > 0) return `${hours}시간 ${mins}분`;
    return `${hours}시간`;
  };

  // 그리드 컴포넌트의 엑셀 다운기능
  const excelDown = () => {
    if (payData.length == 0) {
      setModalState({
        isModalShow: true,
        title: 'Excel 다운',
        desc: '급여 데이터가 없습니다.',
      });
    } else {
      if (AgGridRef.current) {
        const timeColumns = new Set(['total_work_hours', 'total_break_hours']);
        AgGridRef.current.api.exportDataAsExcel({
          fileName: `${storeName}_${payType.label}직급여(${moment(currentMonth).format('YYYY년MM월')})`,
          sheetName: '샵솔',
          headerRowHeight: 30,
          autoConvertFormulas: true,
          processCellCallback: (params) => {
            const col = params.column;
            const field = col?.getColDef()?.field ?? col?.getColId();
            if (field && timeColumns.has(field)) {
              const val = params.value;
              if (typeof val === 'number') return formatMinutesToTimeText(val);
              if (typeof val === 'string' && val.includes('시간')) return val; // 이미 포맷된 경우
            }
            return params.value;
          },
        });
      }
    }
  };

  const handleColumns = (payType: IOption) => {
    if (payType.label === '월급') {
      setColumns([
        {
          field: 'NUMBER',
          headerName: '번호',
          pinned: 'left',
          width: 100,
          minWidth: 100,
          valueFormatter: ({ value }) => value,
        },
        {
          field: 'employee_name',
          headerName: '성명',
          pinned: 'left',
          width: 100,
          minWidth: 100,
        },
        {
          field: 'deduction_type',
          headerName: '공제유형',
          width: 100,
          minWidth: 100,
          cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
            if (data.employee_name === '합계') return '';
            return value === '1' ? '프리랜서' : value === '2' ? '4대보험' : '없음';
          },
        },
        {
          headerName: '급여내역',
          children: [
            {
              field: 'total_salary',
              headerName: '기본급 (A)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '기본급',
                subText: '(A)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'meals',
              headerName: '식대 (B)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '식대',
                subText: '(B)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'self_driving',
              headerName: '자가운전(비과세) (C)',
              minWidth: 120,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '자가운전(비과세)',
                subText: '(C)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'bonus',
              headerName: '상여 (D)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '상여',
                subText: '(D)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'incentive',
              headerName: '성과급 (E)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '성과급',
                subText: '(E)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'total_salary',
              headerName: '총지급액 (F)=(A+B+C+D+E)',
              minWidth: 110,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '총지급액',
                subText: '(F)=(A+B+C+D+E)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          headerName: '공제내역',
          children: [
            {
              field: 'pension_pay',
              headerName: '국민연금',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'health_pay',
              headerName: '건강보험',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'long_term_care_pay',
              headerName: '장기요양',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'employment_pay',
              headerName: '고용보험',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'income_tax',
              headerName: '소득세',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'local_income_tax',
              headerName: '지방소득세',
              minWidth: 110,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'total_withholdings',
              headerName: '공제합계',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          field: 'total_payment_amount',
          headerName: '실지급액',
          minWidth: 110,
          valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
          cellStyle: {
            textAlign: 'right',
          },
        },
        {
          headerName: '비고',
          children: [
            {
              field: 'employment_dates',
              headerName: '입사일/퇴사일',
              minWidth: 130,
              valueGetter: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.employment_start_date && data.employment_end_date) {
                  return `${data.employment_start_date} / ${data.employment_end_date}`;
                }
                return '';
              },
            },
          ],
        },
      ]);
    } else if (payType.label === '일급') {
      setColumns([
        {
          field: 'NUMBER',
          headerName: '번호',
          pinned: 'left',
          width: 100,
          minWidth: 100,
          valueFormatter: ({ value }) => value,
        },
        {
          field: 'employee_name',
          headerName: '성명',
          pinned: 'left',
          width: 100,
          minWidth: 100,
        },
        {
          field: 'deduction_type',
          headerName: '공제유형',
          width: 100,
          minWidth: 100,
          cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
            if (data.employee_name === '합계') return '';
            return value === '1' ? '프리랜서' : value === '2' ? '4대보험' : '없음';
          },
        },
        {
          headerName: '급여내역',
          children: [
            {
              field: 'hourly_wage',
              headerName: '일급 (A)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '일급',
                subText: '(A)',
              },
              cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
                if (data.employee_name === '합계') return '';
                return addComma(String(Number(value) || 0));
              },
            },
            {
              field: 'work_days',
              headerName: '근무일수 (B)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '근무일수',
                subText: '(B)',
              },
              cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
                if (data.employee_name === '합계') return '';
                return addComma(String(Number(value) || 0));
              },
            },
            {
              field: 'total_salary',
              headerName: '총지급액 (C)=(A) x (B)',
              minWidth: 110,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '총지급액',
                subText: '(C)=(A) x (B)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          headerName: '공제내역',
          children: [
            {
              field: 'pension_pay',
              headerName: '국민연금',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'health_pay',
              headerName: '건강보험',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'long_term_care_pay',
              headerName: '장기요양',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'employment_pay',
              headerName: '고용보험',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'income_tax',
              headerName: '소득세',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'local_income_tax',
              headerName: '지방소득세',
              minWidth: 110,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'total_withholdings',
              headerName: '공제합계',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          field: 'total_payment_amount',
          headerName: '실지급액',
          minWidth: 110,
          valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
          cellStyle: {
            textAlign: 'right',
          },
        },
        {
          headerName: '비고',
          children: [
            {
              field: 'employment_dates',
              headerName: '입사일/퇴사일',
              minWidth: 130,
              valueGetter: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.employment_start_date && data.employment_end_date) {
                  return `${data.employment_start_date} / ${data.employment_end_date}`;
                }
                return '';
              },
            },
          ],
        },
      ]);
    } else {
      setColumns([
        {
          field: 'NUMBER',
          headerName: '번호',
          pinned: 'left',
          width: 100,
          minWidth: 100,
          valueFormatter: ({ value }) => value,
        },
        {
          field: 'employee_name',
          headerName: '성명',
          pinned: 'left',
          width: 100,
          minWidth: 100,
        },
        {
          field: 'deduction_type',
          headerName: '공제유형',
          width: 100,
          minWidth: 100,
          cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
            if (data.employee_name === '합계') return '';
            return value === '1' ? '프리랜서' : value === '2' ? '4대보험' : '없음';
          },
        },
        {
          headerName: '급여내역',
          children: [
            {
              field: 'hourly_wage',
              headerName: '시급 (A)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '시급',
                subText: '(A)',
              },
              cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
                if (data.employee_name === '합계') return '';
                return addComma(String(Number(value) || 0));
              },
            },
            {
              field: 'work_days',
              headerName: '근무일수',
              minWidth: 100,
              cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
                if (data.employee_name === '합계') return '';
                return value;
              },
            },
            {
              field: 'total_work_hours',
              headerName: '총근무시간 (B)',
              minWidth: 110,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '총근무시간',
                subText: '(B)',
              },
              cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
                if (data.employee_name === '합계') return '';
                const minutes = Number(value) || 0;
                const hours = Math.floor(minutes / 60);
                const mins = minutes % 60;
                return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
              },
            },
            {
              field: 'total_break_hours',
              headerName: '휴게시간 (C)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '휴게시간',
                subText: '(C)',
              },
              cellRenderer: ({ data, value }: { data: IPayrollRegisterEmpInfo; value: any }) => {
                if (data.employee_name === '합계') return '';
                const minutes = Number(value) || 0;
                const hours = Math.floor(minutes / 60);
                const mins = minutes % 60;
                return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
              },
            },
            {
              field: 'base_pay',
              headerName: '기본급 (D)=(A x (B - C))',
              minWidth: 120,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '기본급',
                subText: '(D)=(A x (B - C))',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          headerName: '수당내역',
          children: [
            {
              field: 'weekly_holiday_pay_total',
              headerName: '주휴수당 (a)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '주휴수당',
                subText: '(a)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (
                  data.weekly_holiday_pay_detail &&
                  data.weekly_holiday_pay_detail.allarray.length > 0 &&
                  data.weekly_holiday_pay_total !== 0
                ) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() => openWeeklyHolidayPayModal(data.weekly_holiday_pay_detail.allarray)}>
                      {addComma(String(data.weekly_holiday_pay_total))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.weekly_holiday_pay_total))}</div>;
              },
            },
            {
              field: 'daily_overtime_pay_total',
              headerName: '초과수당 (b)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '초과수당',
                subText: '(b)',
                tooltip:
                  '초과수당: 법정 근로시간(1일 8시간)을 초과한 근로시간에 대해\n통상임금의 50% 가산하여 지급하는 수당',
                tooltipIcon: <QuestionIcon />,
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.daily_overtime_detail && data.daily_overtime_detail.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        openDetailModal(data.daily_overtime_detail, '초과수당 내역', [
                          '초과수당 계산법 : 총 초과시간 X 시급 X 50%',
                          '법정 근로시간을 초과하여 근무한 경우 지급되는 추가 수당입니다.',
                        ])
                      }>
                      {addComma(String(data.daily_overtime_pay_total))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.daily_overtime_pay_total))}</div>;
              },
            },
            {
              field: 'night_overtime_pay_total',
              headerName: '야간수당 (c)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '야간수당',
                subText: '(c)',
                tooltip:
                  '야간수당: 오후 10시부터 익일 오전 6시 사이 근로한 시간에 대해\n통상임금의 50% 가산하여 지급하는 수당',
                tooltipIcon: <QuestionIcon />,
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.night_overtime_detail && data.night_overtime_detail.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        openDetailModal(data.night_overtime_detail, '야간수당 내역', [
                          '야간수당 계산법 : 총 야간시간 X 시급 X 50%',
                          '야간수당은 오후 10시 ~ 오전 6시까지(익일) 근무한 시간에 대한 추가 수당입니다.',
                        ])
                      }>
                      {addComma(String(data.night_overtime_pay_total))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.night_overtime_pay_total))}</div>;
              },
            },
            {
              field: 'holiday_work_pay_total',
              headerName: '휴일수당 (d)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '휴일수당',
                subText: '(d)',
                tooltip: '휴일수당: 법정공휴일에 근로한 시간에 대해\n통상임금의 50% 가산하여 지급하는 수당',
                tooltipIcon: <QuestionIcon />,
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.holiday_work_detail && data.holiday_work_detail.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        openDetailModal(data.holiday_work_detail, '휴일수당 내역', [
                          '휴일수당 계산법 : 총 휴일시간 X 시급 X 50%',
                          '주휴일, 근로자의 날, 법정공휴일 등 유급 휴일에 근무했을 경우 지급되는 추가 수당입니다',
                        ])
                      }>
                      {addComma(String(data.holiday_work_pay_total))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.holiday_work_pay_total))}</div>;
              },
            },
            {
              field: 'total_allowances',
              headerName: '수당합계 (E)=(a+b+c+d)',
              minWidth: 110,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '수당합계',
                subText: '(E)=(a+b+c+d)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'late_deduction',
              headerName: '지각차감 (e)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '지각차감',
                subText: '(e)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.late_deduction_pay_detail && data.late_deduction_pay_detail.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        openDetailModal(data.late_deduction_pay_detail, '지각차감 내역', [
                          '지각차감 계산법 : 총 지각시간 X 시급 = 차감 금액',
                        ])
                      }>
                      {addComma(String(data.late_deduction))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.late_deduction))}</div>;
              },
            },
            {
              field: 'early_deduction',
              headerName: '조퇴차감 (f)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '조퇴차감',
                subText: '(f)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.early_deduction_pay_detail && data.early_deduction_pay_detail.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        openDetailModal(data.early_deduction_pay_detail, '조퇴차감 내역', [
                          '조퇴차감 계산법 : 총 조퇴시간 X 시급 = 차감 금액',
                        ])
                      }>
                      {addComma(String(data.early_deduction))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.early_deduction))}</div>;
              },
            },
            {
              field: 'absence_deduction',
              headerName: '결근차감 (g)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '결근차감',
                subText: '(g)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.absence_deduction_pay_detail && data.absence_deduction_pay_detail.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        openDetailModal(data.absence_deduction_pay_detail, '결근차감 내역', [
                          '결근차감 계산법 : 총 결근시간 X 시급 = 차감 금액',
                        ])
                      }>
                      {addComma(String(data.absence_deduction))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.absence_deduction))}</div>;
              },
            },
            {
              field: 'off_day_deduction',
              headerName: '휴무차감 (h)',
              minWidth: 100,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '휴무차감',
                subText: '(h)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),

              cellRenderer: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.off_day_deduction_pay_detail && data.off_day_deduction_pay_detail.length > 0) {
                  return (
                    <div
                      className="weekPayMoreBtn"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 3,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        openDetailModal(data.off_day_deduction_pay_detail, '휴무차감 내역', [
                          '휴무차감 계산법 : 휴무일 근무시간 X 시급',
                          '휴무차감은 지정된 휴무일에 근무한 경우 발생하는 급여 차감입니다.',
                        ])
                      }>
                      {addComma(String(data.off_day_deduction))}{' '}
                      <IoChatbubbleEllipsesOutline size={16} style={{ marginLeft: 3 }} />
                    </div>
                  );
                }
                return <div>{addComma(String(data.off_day_deduction))}</div>;
              },
            },
            {
              field: 'total_deductions',
              headerName: '차감합계 (F)=(e+f+g+h)',
              minWidth: 110,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '차감합계',
                subText: '(F)=(e+f+g+h)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },

        {
          headerName: '총 지급액',
          children: [
            {
              field: 'total_salary',
              headerName: '총 지급액 (G)=(D+E-F)',
              minWidth: 110,
              headerComponent: CustomHeaderWithSubtext,
              headerComponentParams: {
                mainText: '총 지급액',
                subText: '(G)=(D+E-F)',
              },
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          headerName: '공제내역',
          children: [
            {
              field: 'pension_pay',
              headerName: '국민연금',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'health_pay',
              headerName: '건강보험',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'long_term_care_pay',
              headerName: '장기요양',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'employment_pay',
              headerName: '고용보험',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'income_tax',
              headerName: '소득세',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'local_income_tax',
              headerName: '지방소득세',
              minWidth: 110,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
            {
              field: 'total_withholdings',
              headerName: '공제합계',
              minWidth: 100,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          headerName: '실지급액',
          children: [
            {
              field: 'total_payment_amount',
              headerName: '실지급액',
              minWidth: 110,
              valueFormatter: ({ value }) => addComma(String(Number(value) || 0)),
            },
          ],
        },
        {
          headerName: '비고',
          children: [
            {
              field: 'employment_dates',
              headerName: '입사일/퇴사일',
              minWidth: 130,
              valueGetter: ({ data }: { data: IPayrollRegisterEmpInfo }) => {
                if (data.employment_start_date && data.employment_end_date) {
                  return `${data.employment_start_date} / ${data.employment_end_date}`;
                }
                return '';
              },
            },
          ],
        },
      ]);
    }
  };

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      filter: true,
      filterParams: {
        button: ['reset'],
        closeOnApply: true,
      },
      cellStyle: {
        textAlign: 'center',
      },
    };
  }, []);

  const AgGridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    fetchEmpsPay();
    handleColumns(payType);
  }, [currentMonth, payType?.value]);
  return (
    <>
      <WithSideBarAndNavBar>
        <div className="flex w-full flex-1 flex-col">
          <strong className="text-[28px]">급여대장</strong>

          <div className="mt-[30px] flex">
            <div className="mr-2.5">
              <MonthInput onChange={handleDateChange} typeText value={currentMonth} />
            </div>
            <div>
              <Select
                className="min-h-10 w-[170px] rounded-lg border border-[#e9e9e9] bg-white"
                components={{
                  IndicatorSeparator: () => null,
                  DropdownIndicator: () => (
                    <div style={{ paddingRight: '15px' }}>
                      <ArrowDownIcon />
                    </div>
                  ),
                  Control: ({ children, ...props }: any) => {
                    return (
                      <components.Control {...props}>
                        <div
                          style={{
                            paddingLeft: '10px',
                            fontFamily: 'Spoqa Han Sans Neo',
                            fontWeight: '500',
                            fontSize: '16px',
                            color: '#868686',
                          }}>
                          급여유형
                        </div>
                        {children}
                      </components.Control>
                    );
                  },
                }}
                isSearchable={false}
                placeholder="시급"
                styles={colorStyles}
                options={options}
                value={payType}
                onChange={handlePayrollInformation}
              />
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-2.5 rounded-2xl bg-white p-[2%]">
            <div className="flex">
              <div>
                <div className="text-sm font-bold leading-5 text-[#999999]">
                  급여정산일 :{' '}
                  <span className="text-sm font-bold leading-5 text-[#075453]">
                    매달 {calDay === '말일' ? calDay : calDay + '일'}
                  </span>
                </div>
                <div className="text-sm font-bold leading-5 text-[#999999]">
                  급여산정기간 :{' '}
                  <span className="text-sm font-bold leading-5 text-[#075453]">
                    {moment(calPeriodStart).format('YYYY년MM월DD일')} ~ {moment(calPeriodEnd).format('YYYY년MM월DD일')}
                  </span>
                </div>
              </div>
              <div className="ml-auto">
                <Button type="type8" height={38} label="Excel 다운" action={excelDown} />
              </div>
            </div>

            <AgGrid
              AgGridRef={AgGridRef}
              rowData={payData}
              colDefs={columns}
              defaultColDef={defaultColDef}
              pinnedBottomRowData={pinnedBottomRowData}
            />
          </div>
          <div className="mt-[30px]">
            <div className="text-xs font-bold leading-[18px] text-[#505054]">아래 사항을 참고해 주세요!</div>

            <div className="text-xs font-normal leading-[18px] text-[#999999]">
              {`- 시급직 시본급 계산식 ={시급※ 근무시간(시간) +{시급 760 x 근무시간(분)}`}
            </div>
            <div className="text-xs font-normal leading-[18px] text-[#999999]">{`- 급여대장 엑셀 다운로드 시 보여지는 데이터 그대로 저장됩니다.(정렬, 급여내역, 공제내역, 데이터 개수, 검색, 필터 등)`}</div>
            <div className="text-xs font-normal leading-[18px] text-[#999999]">{`- 원단위 미만 소수점 자리의 값은 올림 처리하여 값을 보여줍니다.`}</div>
          </div>
        </div>
      </WithSideBarAndNavBar>
      <AllowanceDetailModal
        isVisible={detailModal.isVisible}
        onClose={closeDetailModal}
        data={detailModal.data}
        title={detailModal.title}
        infoTitle={detailModal.infoTitle}
        infoDescriptions={detailModal.infoDescriptions}
      />
      <WeeklyHolidayPayModal
        isVisible={weeklyHolidayPayModal.isVisible}
        onClose={closeWeeklyHolidayPayModal}
        data={weeklyHolidayPayModal.data}
      />
    </>
  );
};
export default PayrollRegister;
